import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PieChart,
  Star,
  ChevronDown
} from 'lucide-react';
import '../../styles/Stats.css';
import StreakUpgradeButton from '../../components/StreakUpgradeButton';
import HiraganaLoader from '../../components/HiraganaLoader';
import { API_URL } from '../../app/constants'

function Stats() {
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [diagnosticData, setDiagnosticData] = useState(null);
    const [expandedSection, setExpandedSection] = useState(null);
    const [containerWidth, setContainerWidth] = useState(0);
    
    useEffect(() => {
        Promise.all([
            fetchAnalyticsData(),
            fetchDiagnosticData()
        ]);
        
        // Measure container width
        const updateContainerWidth = () => {
            // Use window width to properly detect desktop vs mobile
            const windowWidth = window.innerWidth;
            setContainerWidth(windowWidth);
            console.log('Updated container width to window width:', windowWidth);
        };
        
        // Initial measurement
        setTimeout(updateContainerWidth, 100);
        
        // Listen to window resize
        window.addEventListener('resize', updateContainerWidth);
        return () => window.removeEventListener('resize', updateContainerWidth);
    }, []);

    const fetchAnalyticsData = async () => {
        try {
            const jwt = localStorage.getItem('jwt');
            if (!jwt) {
                throw new Error("JWT token not found in localStorage");
            }
            const response = await fetch(`${API_URL}/api/v1/panel/user/analytics`, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            console.log(data);
            setAnalyticsData(data);
        } catch (error) {
            console.error("Error fetching analytics data:", error);
            setError("Error fetching analytics data");
        } finally {
            // Loading removed
        }
    };

    const fetchDiagnosticData = async () => {
        try {
            const jwt = localStorage.getItem('jwt');
            if (!jwt) {
                throw new Error("JWT token not found in localStorage");
            }
            const response = await fetch(`${API_URL}/api/v1/panel/diagnostic/analytics`, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            console.log('Diagnostic data:', data);
            setDiagnosticData(data);
        } catch (error) {
            console.error("Error fetching diagnostic data:", error);
        }
    };


    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };



    const getSectionFullName = (section) => {
        const names = {
            'grammar': 'Grammar',
            'vocabulary': 'Vocabulary', 
            'reading': 'Reading'
        };
        return names[section.toLowerCase()] || capitalizeFirstLetter(section);
    };

    // Adaptive sizing with better desktop support
    const getAdaptiveSizing = (itemCount, availableWidth) => {
        if (!itemCount || itemCount === 0) return null;
        
        const isMobile = containerWidth <= 768;
        const isTablet = containerWidth <= 1024 && containerWidth > 768;
        const isDesktop = containerWidth > 1024;
        
        // Better sizing for different screen sizes
        const minItemWidth = isMobile ? 60 : isTablet ? 80 : 100;
        const maxItemWidth = isMobile ? 80 : isTablet ? 120 : 180;
        const yAxisSpace = isMobile ? 40 : 60;
        const containerPadding = isMobile ? 20 : 40;
        const gapBetweenItems = isMobile ? 8 : isTablet ? 12 : 16;
        
        const chartArea = Math.max(availableWidth - yAxisSpace - containerPadding, isMobile ? 200 : 300);
        const totalGapSpace = (itemCount - 1) * gapBetweenItems;
        const spaceForItems = chartArea - totalGapSpace;
        const idealItemWidth = spaceForItems / itemCount;
        
        const needsScroll = idealItemWidth < minItemWidth;
        
        if (isMobile || (needsScroll && !isDesktop)) {
            return {
                useScroll: true,
                itemWidth: `${minItemWidth}px`,
                barWidth: isMobile ? '24px' : isTablet ? '32px' : '40px',
                fontSize: isMobile ? '9px' : isTablet ? '11px' : '12px',
                titleFontSize: isMobile ? '11px' : isTablet ? '13px' : '14px',
                justifyContent: 'flex-start',
                flex: `0 0 ${minItemWidth}px`,
                gap: `${gapBetweenItems}px`,
                totalWidth: `${itemCount * minItemWidth + totalGapSpace}px`
            };
        } else {
            const finalItemWidth = Math.min(idealItemWidth, maxItemWidth);
            const barWidth = Math.min(finalItemWidth * 0.4, isDesktop ? 60 : 45);
            
            return {
                useScroll: false,
                itemWidth: `${Math.floor(finalItemWidth)}px`,
                barWidth: `${Math.floor(barWidth)}px`,
                fontSize: finalItemWidth >= 140 ? '13px' : finalItemWidth >= 100 ? '12px' : '11px',
                titleFontSize: finalItemWidth >= 140 ? '16px' : finalItemWidth >= 100 ? '15px' : '14px',
                justifyContent: 'space-between',
                flex: '1',
                gap: `${gapBetweenItems}px`,
                totalWidth: '100%'
            };
        }
    };

    
    if (error) return <div className="error-message">{error}</div>;
    if (!analyticsData) return <HiraganaLoader />;

    return (
        <div className="analytics">
            <StreakUpgradeButton source="Analytics" />
            <div className="dashboard-header">
                <h1>Analytics</h1>
            </div>

            {/* Score Summary Card */}
            <div className="analytics-section">
                <div className="section-title">
                    <Star size={18} className="section-icon" />
                    <h2>Performance Summary</h2>
                </div>
                
                <div className="summary-grid">
                    <div className="summary-stat">
                        <div className="stat-header">Overall Score</div>
                        <div className="stat-value">{analyticsData.overall_score_percentage || 0}%</div>
                        <div className="stat-subtitle">{analyticsData.total_correct_answers || 0} of {analyticsData.total_questions || 0} questions</div>
                    </div>
                    
                    <div className="summary-stat">
                        <div className="stat-header">Completed Sessions</div>
                        <div className="stat-value">{analyticsData.total_personalized_exams || 0}</div>
                        <div className="stat-subtitle">personalized trainings</div>
                    </div>
                </div>
            </div>
            
            {/* Section Performance */}
            <div className="analytics-section performance-section-large">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: containerWidth <= 768 ? 'flex-start' : 'center', flexDirection: containerWidth <= 768 ? 'column' : 'row'}}>
                    <div className="section-title">
                        <PieChart size={18} className="section-icon" />
                        <h2>Performance Overview</h2>
                    </div>
                    
                    {/* Legend in top right */}
                    <div style={{display: 'flex', gap: '20px', fontSize: '13px', marginTop: containerWidth <= 768 ? '15px' : '0'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <div style={{width: '12px', height: '12px', backgroundColor: 'rgba(147, 51, 233, 0.6)', borderRadius: '3px'}}></div>
                            <span>Diagnostic</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <div style={{width: '12px', height: '12px', backgroundColor: '#9334E9', borderRadius: '3px'}}></div>
                            <span>Training</span>
                        </div>
                    </div>
                </div>
                
                {/* Clean Performance Chart */}
                <div style={{marginTop: '40px'}}>
                    <div style={{display: 'flex', alignItems: 'end', gap: '16px'}}>
                        {/* Y-axis */}
                        <div style={{
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'space-between', 
                            height: containerWidth <= 768 ? '140px' : '180px', 
                            fontSize: containerWidth <= 768 ? '10px' : '12px', 
                            color: '#666',
                            width: containerWidth <= 768 ? '35px' : '40px',
                            textAlign: 'right'
                        }}>
                            <span>100%</span>
                            <span>50%</span>
                            <span>0%</span>
                        </div>
                        
                        {/* Chart Container */}
                        <div style={{ flex: 1 }}>
                            {(() => {
                                // Get sections data
                                const allSections = new Set();
                                if (analyticsData?.average_scores?.sections) {
                                    Object.keys(analyticsData.average_scores.sections).forEach(section => allSections.add(section));
                                }
                                if (diagnosticData?.section_scores) {
                                    Object.keys(diagnosticData.section_scores).forEach(section => allSections.add(section));
                                }
                                
                                const sectionsArray = Array.from(allSections);
                                const adaptiveStyle = getAdaptiveSizing(sectionsArray.length, containerWidth - 100);
                                
                                console.log('=== Performance Overview Debug ===');
                                console.log('Container width:', containerWidth);
                                console.log('Is desktop:', containerWidth > 1024);
                                console.log('Is mobile:', containerWidth <= 768);
                                console.log('Sections count:', sectionsArray.length);
                                console.log('Use scroll:', adaptiveStyle?.useScroll);
                                console.log('Item width:', adaptiveStyle?.itemWidth);
                                console.log('Bar width:', adaptiveStyle?.barWidth);
                                console.log('================================');
                                
                                if (!adaptiveStyle || sectionsArray.length === 0) {
                                    return <div style={{textAlign: 'center', color: '#666'}}>No data available</div>;
                                }
                                
                                // Normal chart layout
                                return (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: adaptiveStyle.justifyContent,
                                        alignItems: 'end',
                                        gap: adaptiveStyle.gap,
                                        height: '180px',
                                        overflowX: adaptiveStyle.useScroll ? 'auto' : 'visible',
                                        width: '100%'
                                    }}>
                                        {sectionsArray.map((section) => {
                                            const trainingScore = analyticsData?.average_scores?.sections?.[section] || 0;
                                            const diagnosticScore = diagnosticData?.section_scores?.[section]?.score_percentage || 0;
                                            
                                            return (
                                                <div key={section} style={{
                                                    display: 'flex',
                                                    alignItems: 'end',
                                                    gap: '6px',
                                                    flex: adaptiveStyle.flex,
                                                    width: adaptiveStyle.itemWidth,
                                                    justifyContent: 'center'
                                                }}>
                                                    {/* Diagnostic Bar */}
                                                    <div style={{
                                                        height: `${Math.max((diagnosticScore / 100) * 160, 20)}px`,
                                                        width: adaptiveStyle.barWidth,
                                                        backgroundColor: 'rgba(147, 51, 233, 0.6)',
                                                        borderRadius: '6px 6px 0 0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'white',
                                                        fontSize: adaptiveStyle.fontSize,
                                                        fontWeight: '600',
                                                        transition: 'all 0.3s ease'
                                                    }}>
                                                        {diagnosticScore}%
                                                    </div>
                                                    
                                                    {/* Training Bar */}
                                                    <div style={{
                                                        height: `${Math.max((trainingScore / 100) * 160, 20)}px`,
                                                        width: adaptiveStyle.barWidth,
                                                        backgroundColor: '#9334E9',
                                                        borderRadius: '6px 6px 0 0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'white',
                                                        fontSize: adaptiveStyle.fontSize,
                                                        fontWeight: '600',
                                                        transition: 'all 0.3s ease'
                                                    }}>
                                                        {Math.round(trainingScore)}%
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
                    
                {/* Clean Section Titles */}
                <div style={{marginTop: '20px', display: 'flex', alignItems: 'center', gap: '16px'}}>
                    {/* Spacer for Y-axis */}
                    <div style={{width: '40px'}}></div>
                    
                    {/* Titles Container */}
                    <div style={{ flex: 1 }}>
                        {(() => {
                            const allSections = new Set();
                            if (analyticsData?.average_scores?.sections) {
                                Object.keys(analyticsData.average_scores.sections).forEach(section => allSections.add(section));
                            }
                            if (diagnosticData?.section_scores) {
                                Object.keys(diagnosticData.section_scores).forEach(section => allSections.add(section));
                            }
                            
                            const sectionsArray = Array.from(allSections);
                            const adaptiveStyle = getAdaptiveSizing(sectionsArray.length, containerWidth - 100);
                            
                            if (!adaptiveStyle || sectionsArray.length === 0) return null;
                            
                            return (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: adaptiveStyle.justifyContent,
                                    alignItems: 'center',
                                    gap: adaptiveStyle.gap,
                                    width: '100%',
                                    overflowX: adaptiveStyle.useScroll ? 'auto' : 'visible'
                                }}>
                                    {sectionsArray.map((section) => (
                                        <div key={section} 
                                             style={{
                                                 fontSize: adaptiveStyle.titleFontSize, 
                                                 fontWeight: '600', 
                                                 color: expandedSection === section ? '#9334E9' : '#333', 
                                                 textAlign: 'center', 
                                                 cursor: 'pointer', 
                                                 display: 'flex', 
                                                 alignItems: 'center', 
                                                 justifyContent: 'center',
                                                 gap: '4px',
                                                 flex: adaptiveStyle.flex,
                                                 width: adaptiveStyle.itemWidth,
                                                 transition: 'color 0.3s ease',
                                                 padding: '8px 4px'
                                             }} 
                                         onClick={() => {
                                             const newSection = expandedSection === section ? null : section;
                                             setExpandedSection(newSection);
                                         }}
                                         onMouseEnter={(e) => e.target.style.color = '#9334E9'}
                                         onMouseLeave={(e) => e.target.style.color = '#333'}>
                                            <span>{getSectionFullName(section)}</span>
                                            <ChevronDown 
                                                size={16} 
                                                style={{
                                                    color: '#9334E9', 
                                                    transition: 'transform 0.3s ease', 
                                                    transform: expandedSection === section ? 'rotate(180deg)' : 'rotate(0deg)'
                                                }} 
                                            />
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>
                </div>
                
                {/* Expanded Category Details */}
                {expandedSection && (
                    <div style={{marginTop: '24px', borderTop: '1px solid #E5E7EB', paddingTop: '24px'}}>
                        <div style={{fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '16px', textAlign: 'center'}}>
                            Detailed Breakdown
                        </div>
                        
                        {(() => {
                            if (containerWidth <= 768) {
                                return (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '20px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        color: '#666',
                                        fontSize: '14px'
                                    }}>
                                        For better detailed breakdown visualization, please view on desktop or tablet.
                                    </div>
                                );
                            }
                            
                            return (() => {
                            // Get categories for expanded section
                            let sectionCategories = {
                                grammar: [],
                                vocabulary: [],
                                reading: []
                            };
                            
                            if (analyticsData?.section_categories) {
                                sectionCategories = {...sectionCategories, ...analyticsData.section_categories};
                            }
                            if (diagnosticData?.section_categories) {
                                Object.keys(diagnosticData.section_categories).forEach(section => {
                                    const diagnosticCats = diagnosticData.section_categories[section] || [];
                                    const existingCats = sectionCategories[section] || [];
                                    sectionCategories[section] = [...new Set([...existingCats, ...diagnosticCats])];
                                });
                            }
                            
                            const categories = sectionCategories[expandedSection] || [];
                            
                            if (!categories || categories.length === 0) {
                                return (
                                    <div style={{textAlign: 'center', color: '#666', marginTop: '20px'}}>
                                        No {expandedSection} categories found. Complete your trainings to see detailed breakdown.
                                    </div>
                                );
                            }
                            
                            return (
                                <div>
                                    {/* Clean Detailed Chart */}
                                    <div style={{marginTop: '20px', display: 'flex', alignItems: 'end', gap: '16px'}}>
                                        {/* Y-axis */}
                                        <div style={{
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            justifyContent: 'space-between', 
                                            height: containerWidth <= 768 ? '120px' : '160px', 
                                            fontSize: containerWidth <= 768 ? '10px' : '12px', 
                                            color: '#666',
                                            width: containerWidth <= 768 ? '35px' : '40px',
                                            textAlign: 'right'
                                        }}>
                                            <span>100%</span>
                                            <span>50%</span>
                                            <span>0%</span>
                                        </div>
                                        
                                        {/* Chart Container */}
                                        <div style={{ flex: 1 }}>
                                            {(() => {
                                                const adaptiveStyle = getAdaptiveSizing(categories.length, containerWidth - 100);
                                                
                                                if (!adaptiveStyle) return null;
                                                
                                                return (
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: adaptiveStyle.justifyContent,
                                                        alignItems: 'end',
                                                        gap: adaptiveStyle.gap,
                                                        height: containerWidth <= 768 ? '120px' : '160px',
                                                        overflowX: adaptiveStyle.useScroll ? 'auto' : 'visible',
                                                        width: '100%'
                                                    }}>
                                                        {categories.map((category) => {
                                                            const trainingScore = analyticsData?.average_scores?.categories?.[category] || 0;
                                                            const diagnosticScore = diagnosticData?.category_scores?.[category]?.score_percentage || 0;
                                                            
                                                            return (
                                                                <div key={category} style={{
                                                                    display: 'flex',
                                                                    alignItems: 'end',
                                                                    gap: '6px',
                                                                    flex: adaptiveStyle.flex,
                                                                    width: adaptiveStyle.itemWidth,
                                                                    justifyContent: 'center'
                                                                }}>
                                                                    {/* Diagnostic Bar */}
                                                                    <div style={{
                                                                        height: `${Math.max((diagnosticScore / 100) * (containerWidth <= 768 ? 100 : 140), 20)}px`,
                                                                        width: adaptiveStyle.barWidth,
                                                                        backgroundColor: 'rgba(147, 51, 233, 0.6)',
                                                                        borderRadius: '6px 6px 0 0',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        color: 'white',
                                                                        fontSize: adaptiveStyle.fontSize,
                                                                        fontWeight: '600',
                                                                        transition: 'all 0.3s ease'
                                                                    }}>
                                                                        {diagnosticScore}%
                                                                    </div>
                                                                    
                                                                    {/* Training Bar */}
                                                                    <div style={{
                                                                        height: `${Math.max((trainingScore / 100) * (containerWidth <= 768 ? 100 : 140), 20)}px`,
                                                                        width: adaptiveStyle.barWidth,
                                                                        backgroundColor: '#9334E9',
                                                                        borderRadius: '6px 6px 0 0',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        color: 'white',
                                                                        fontSize: adaptiveStyle.fontSize,
                                                                        fontWeight: '600',
                                                                        transition: 'all 0.3s ease'
                                                                    }}>
                                                                        {Math.round(trainingScore)}%
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    
                                    {/* Clean Category Titles */}
                                    <div style={{marginTop: '16px', display: 'flex', alignItems: 'center', gap: '16px'}}>
                                        {/* Spacer for Y-axis */}
                                        <div style={{width: '40px'}}></div>
                                        
                                        {/* Titles Container */}
                                        <div style={{ flex: 1 }}>
                                            {(() => {
                                                const adaptiveStyle = getAdaptiveSizing(categories.length, containerWidth - 100);
                                                
                                                if (!adaptiveStyle) return null;
                                                
                                                return (
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: adaptiveStyle.justifyContent,
                                                        alignItems: 'center',
                                                        gap: adaptiveStyle.gap,
                                                        width: '100%',
                                                        overflowX: adaptiveStyle.useScroll ? 'auto' : 'visible'
                                                    }}>
                                                        {categories.map((category) => (
                                                            <div key={category} style={{
                                                                fontSize: adaptiveStyle.titleFontSize, 
                                                                fontWeight: '600', 
                                                                color: '#333', 
                                                                textAlign: 'center',
                                                                lineHeight: '1.2',
                                                                wordWrap: 'break-word',
                                                                flex: adaptiveStyle.flex,
                                                                width: adaptiveStyle.itemWidth,
                                                                padding: '4px'
                                                            }}>
                                                                {capitalizeFirstLetter(category)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            );
                        })();
                        })()}
                    </div>
                )}
                
                
                {/* Fallback message */}
                {(!diagnosticData?.section_scores || Object.keys(diagnosticData.section_scores).length === 0) && 
                 (!analyticsData?.average_scores?.sections || Object.keys(analyticsData.average_scores.sections).length === 0) && (
                    <div className="empty-message" style={{textAlign: 'center', marginTop: '40px'}}>
                        Continue your training to identify strengths.
                    </div>
                )}
            </div>
            
            
            {/* Skills Progress Table */}
            <div className="analytics-section">
                <div className="section-title">
                    <PieChart size={18} className="section-icon" />
                    <h2>Skills Progress</h2>
                </div>
                
                <div className="skills-table-container">
                    <table className="skills-table">
                        <thead>
                            <tr>
                                <th>Skill</th>
                                <th>Progress</th>
                                <th>Next Steps</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                // Get all categories from all sections
                                const allSkills = [];
                                
                                // Collect from training data
                                if (analyticsData?.average_scores?.categories) {
                                    Object.entries(analyticsData.average_scores.categories).forEach(([category, score]) => {
                                        allSkills.push({
                                            name: category,
                                            trainingScore: score,
                                            diagnosticScore: diagnosticData?.category_scores?.[category]?.score_percentage || 0
                                        });
                                    });
                                }
                                
                                // Add diagnostic-only categories
                                if (diagnosticData?.category_scores) {
                                    Object.entries(diagnosticData.category_scores).forEach(([category, data]) => {
                                        if (!allSkills.find(skill => skill.name === category)) {
                                            allSkills.push({
                                                name: category,
                                                trainingScore: 0,
                                                diagnosticScore: data.score_percentage || 0
                                            });
                                        }
                                    });
                                }
                                
                                if (allSkills.length === 0) {
                                    return (
                                        <tr>
                                            <td colSpan="3" className="empty-table-message">
                                                Complete your diagnostic test and trainings to see your skills progress
                                            </td>
                                        </tr>
                                    );
                                }
                                
                                return allSkills.map((skill, index) => {
                                    // Use the higher score between training and diagnostic
                                    const bestScore = Math.max(skill.trainingScore, skill.diagnosticScore);
                                    const isStrong = bestScore >= 70; // Green if 70% or above
                                    const progressColor = isStrong ? '#10B981' : '#F97316'; // Green or Orange
                                    
                                    return (
                                        <tr key={skill.name}>
                                            <td className="skill-name">
                                                {capitalizeFirstLetter(skill.name)}
                                            </td>
                                            <td className="skill-progress">
                                                <div className="progress-indicator" style={{color: progressColor}}>
                                                    {bestScore.toFixed(0)}%
                                                </div>
                                            </td>
                                            <td className="skill-next-step">
                                                {isStrong ? (
                                                    <span className="keep-going">Keep going!</span>
                                                ) : (
                                                    <button 
                                                        className="practice-button"
                                                        onClick={() => navigate('/practice')}
                                                    >
                                                        Practice now
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                });
                            })()}
                        </tbody>
                    </table>
                </div>
                
                <div className="info-box">
                    Your next personalized trainings will focus on your areas for improvement.
                </div>
            </div>

        </div>
    );
}

export default Stats;